import React, { ReactNode } from 'react'
import {
  Stat,
  StatHelpText,
  StatLabel,
  VStack,
  GridItem,
  Spinner,
  Spacer,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Icon,
} from '@chakra-ui/react'
import {
  DOPPEL_DARK_CLICKABLE,
  DOPPEL_DARK_SECONDARY,
  DOPPEL_GREY,
  DOPPEL_DARK_CLICKABLE_HOVER,
} from '@/utils/style'
import { InfoOutlineIcon } from '@chakra-ui/icons'
import ErrorComponent from '@/components/error_component'
import { PiMagnifyingGlassPlusLight } from 'react-icons/pi'

export enum WidgetLayout {
  CONTENT_TOP = 'content_top',
  CONTENT_BOTTOM = 'content_bottom',
}

type WidgetProps = {
  label: string
  helpText: string
  rowSpan: number
  colSpan: number
  layout?: WidgetLayout
  infoContent?: ReactNode
  loading?: boolean
  error?: any
  emptyData?: boolean
  children: ReactNode
  onClick?: () => void
}

const Widget: React.FC<WidgetProps> = ({
  label,
  helpText,
  rowSpan,
  colSpan,
  layout = WidgetLayout.CONTENT_BOTTOM,
  infoContent,
  loading = false,
  error,
  emptyData = false,
  children,
  onClick,
  ...props
}) => {
  const innerContent = loading ? (
    <WidgetSpinner />
  ) : error ? (
    <WidgetError />
  ) : emptyData ? (
    <WidgetEmpty />
  ) : (
    children
  )

  let widgetContent = null
  switch (layout) {
    case WidgetLayout.CONTENT_TOP: {
      widgetContent = (
        <Flex align="flex-start" flexDirection="column" height="100%">
          {innerContent}

          <Spacer />

          <WidgetDescription helpText={helpText} label={label} />
        </Flex>
      )
      break
    }
    case WidgetLayout.CONTENT_BOTTOM: {
      widgetContent = (
        <Flex align="flex-start" flexDirection="column" height="100%">
          <WidgetDescription helpText={helpText} label={label} />

          {innerContent}
        </Flex>
      )
      break
    }
  }
  return (
    <GridItem
      {...props}
      _hover={onClick ? { bg: DOPPEL_DARK_CLICKABLE_HOVER } : undefined}
      bg={DOPPEL_DARK_SECONDARY}
      colSpan={colSpan}
      cursor={onClick ? 'pointer' : 'default'}
      key={`widget-${colSpan}`}
      onClick={onClick}
      padding={4}
      position="relative"
      rowSpan={rowSpan}
    >
      {onClick ? (
        <Icon
          as={PiMagnifyingGlassPlusLight}
          boxSize="17.5px"
          name="view more details"
          position="absolute"
          right={infoContent ? 7 : 2}
          top="7px"
        />
      ) : null}

      <Stat height="100%" sx={{ '> dl': { height: '100%' } }}>
        {widgetContent}
      </Stat>

      {infoContent && (
        <Popover trigger="hover">
          <PopoverTrigger>
            <InfoOutlineIcon
              boxSize={3.5}
              color={DOPPEL_GREY}
              name="info"
              position="absolute"
              right={2}
              top={2}
            />
          </PopoverTrigger>

          <PopoverContent
            bg={DOPPEL_DARK_CLICKABLE}
            color={DOPPEL_GREY}
            fontSize={14}
            width="inherit"
          >
            <PopoverBody>{infoContent}</PopoverBody>
          </PopoverContent>
        </Popover>
      )}
    </GridItem>
  )
}

/* helper components */

const WidgetSpinner = () => {
  return (
    <VStack align="center" flexGrow={1} justifyContent="center" width="100%">
      <Spinner />
    </VStack>
  )
}

const WidgetError = () => {
  return (
    <ErrorComponent
      errorText="Problem loading data"
      flexGrow={1}
      fontSize="14px"
      iconSize={6}
      spacing={1}
      stackDirection="column-reverse"
      width="100%"
    />
  )
}

const WidgetEmpty = () => {
  return (
    <ErrorComponent
      errorText="No data available"
      flexGrow={1}
      fontSize="14px"
      hideIcon={true}
      iconSize={6}
      spacing={1}
      stackDirection="column-reverse"
      width="100%"
    />
  )
}

type WidgetDescriptionProps = {
  label: string
  helpText: string
  [key: string]: any
}

const WidgetDescription: React.FC<WidgetDescriptionProps> = ({
  label,
  helpText,
  ...props
}: WidgetDescriptionProps) => {
  return (
    <VStack align="flex-start" spacing={1} {...props}>
      {label && (
        <StatLabel fontSize="14px" fontWeight="semibold" lineHeight={1}>
          {label}
        </StatLabel>
      )}

      {helpText && (
        <StatHelpText fontSize="12px" fontWeight="semibold" lineHeight={1}>
          {helpText}
        </StatHelpText>
      )}
    </VStack>
  )
}

export default Widget
